"use client";
import { useState } from "react";
import { TableCell, TableRow } from "@/app/admin/_common/ui/table";
import { Checkbox } from "@/app/admin/_common/ui/checkbox";
import { Badge } from "@/app/admin/_common/ui/badge";
import GuestTableMenu from "./GuestTableMenu";
import { Ticket, Star } from "lucide-react";
import { cn } from "@/app/_utils/cn";

export default function GuestTableRow({
  order,
  show,
  showId,
  futureShows,
  showColumn,
  liveCheckIn,
  sendclient,
  onChanged,
}: {
  order: any;
  show?: any;
  showId?: string;
  futureShows?: any[];
  showColumn?: boolean;
  liveCheckIn: boolean;
  sendclient: (item: string) => void;
  onChanged?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const { guest_data, attending_show_data, notes } = order;
  const quantity = Number(order.quantity ?? 0);
  const redeemed = Number(order.redeemed ?? 0);
  const expandable = quantity > 1;
  const numShows = guest_data?.num_shows ?? 0;
  const starCount = numShows >= 10 ? 3 : numShows >= 5 ? 2 : numShows > 1 ? 1 : 0;

  function toggleCheckIn(nextChecked: boolean) {
    sendclient(
      JSON.stringify({
        type: "list-check",
        data: { ...order, redeemed: nextChecked ? quantity : 0 },
      })
    );
  }

  function toggleTicketCheckIn(index: number, nextChecked: boolean) {
    sendclient(
      JSON.stringify({
        type: "list-check",
        data: { ...order, redeemed: nextChecked ? redeemed + 1 : redeemed - 1 },
      })
    );
  }

  const colSpan = 5 + (showColumn ? 1 : 0) + (liveCheckIn ? 1 : 0);

  return (
    <>
      <TableRow
        className={cn("[&>td]:py-2 hover:bg-muted/25", expandable && "cursor-pointer")}
        onClick={() => expandable && setExpanded((v) => !v)}
      >
        <TableCell>
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex min-w-0 items-center gap-1.5 text-[13px] text-foreground">
              <span className="truncate">
                {guest_data?.first_name} {guest_data?.last_name}
              </span>
              {order.comped && (
                <Badge className="h-4 flex-none px-1.5 text-[10px] font-normal" variant="default">
                  comped
                </Badge>
              )}
              {starCount > 0 && (
                <span className="flex flex-none items-center gap-0.5">
                  {Array.from({ length: starCount }).map((_, i) => (
                    <Star key={i} className="size-3 fill-amber-400 text-amber-400" />
                  ))}
                </span>
              )}
            </div>
            <div className="flex flex-col gap-0.5 text-[11.5px] text-muted-foreground md:hidden">
              <span className="truncate">{guest_data?.email || "-"}</span>
              <span className="truncate">{guest_data?.instagram || "N/A"}</span>
            </div>
          </div>
        </TableCell>
        <TableCell>
          <span className="flex items-center gap-1 text-[12.5px] text-muted-foreground">
            <Ticket className="size-3.5 flex-none" />
            {quantity}
          </span>
        </TableCell>
        {showColumn && (
          <TableCell className="truncate text-[12.5px] text-muted-foreground">
            {attending_show_data?.title ?? "-"}
          </TableCell>
        )}
        <TableCell className="hidden truncate text-right text-[12.5px] text-muted-foreground md:table-cell">
          {guest_data?.email || "-"}
        </TableCell>
        <TableCell className="hidden truncate text-right text-[12.5px] text-muted-foreground md:table-cell">
          {guest_data?.instagram || "N/A"}
        </TableCell>
        {liveCheckIn && (
          <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={quantity > 0 && redeemed === quantity}
              onCheckedChange={(checked) => toggleCheckIn(Boolean(checked))}
            />
          </TableCell>
        )}
        <TableCell className="pr-8 text-right" onClick={(e) => e.stopPropagation()}>
          <GuestTableMenu
            order={order}
            show={show}
            showId={showId}
            futureShows={futureShows}
            onChanged={onChanged}
          />
        </TableCell>
      </TableRow>

      {expanded && (
        <>
          {notes && (
            <TableRow className="[&>td]:py-2 animate-in fade-in slide-in-from-top-1 duration-200 hover:bg-transparent">
              <TableCell colSpan={colSpan} className="bg-muted/40 text-[12px] italic text-muted-foreground">
                {notes}
              </TableCell>
            </TableRow>
          )}
          {Array.from({ length: quantity }).map((_, index) => (
            <TableRow
              key={index}
              className="[&>td]:py-2 animate-in fade-in slide-in-from-top-1 duration-200 bg-muted/10 hover:bg-muted/20"
              style={{ animationDelay: `${index * 30}ms`, animationFillMode: "backwards" }}
            >
              <TableCell className="text-[12.5px] text-muted-foreground">
                {guest_data?.first_name} {guest_data?.last_name}
              </TableCell>
              <TableCell />
              {showColumn && <TableCell />}
              <TableCell className="hidden md:table-cell" />
              <TableCell className="hidden md:table-cell" />
              {liveCheckIn && (
                <TableCell className="text-center">
                  <Checkbox
                    checked={index < redeemed}
                    onCheckedChange={(checked) => toggleTicketCheckIn(index, Boolean(checked))}
                  />
                </TableCell>
              )}
              <TableCell />
            </TableRow>
          ))}
        </>
      )}
    </>
  );
}
